<template>
  <div class="bg-viewer" :style="styles">
    <pdf-empty-state v-if="!fileLoaded" :empty-size="emptySize" />
    <zt-pdf-viewer
      v-else
      :fixed-rotation="false"
      :src="base64"
      :scale=".50"
      :page="page"
      :increment="zoomQuantity"
      :rotate="rotation"
      :key="viewerKey"
      @viewer-update="_viewerUpdate"
    >
      <div class="w-full h-full tool-container">
        <signature-employee-tool
          ref="sigDef"
          v-if="pdfLoaded && modalState.config.employee.isVisible"
          :document="modalState.config.PDFJSObject"
          :preset="modalState.config.employee.preset"
        />
        <signature-employer-tool
          ref="sigEmp"
          v-if="pdfLoaded && modalState.config.employer.isVisible"
          :re-render="true"
          :document="modalState.config.PDFJSObject"
          :preset="modalState.config.employer.preset"
        />
        <signature-cut-pdf-tool
          v-if="pdfLoaded && modalState.config.cutPdf.preset.show"
          :re-render="true"
          :document="modalState.config.PDFJSObject"
          :preset="modalState.config.cutPdf.preset"
        />
      </div>
    </zt-pdf-viewer>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { findKey, isEqual, isEmpty } from 'lodash'
import SignatureEmployerTool from './tools/SignatureEmployerTool'
import SignatureEmployeeTool from './tools/SignatureEmployeeTool'
import SignatureCutPdfTool from './tools/SignatureCutPdfTool'
import PdfEmptyState from './PDFEmptyState'
import { A4_WIDTH, A4_HEIGHT, SQUARE_SIGNATURE_WIDTH, SQUARE_SIGNATURE_HEIGHT, ZOOM_STEP } from '../../../utils/constants'
import { transformCoordinates, convertLastPage } from '../../../utils'
export default {
  name: "SignaturePdfVisualizer",
  components: {
    PdfEmptyState,
    SignatureEmployeeTool,
    SignatureEmployerTool,
    SignatureCutPdfTool
  },
  provide() {
    return {
      getThumbnailList: this.getThumbnailList
    }
  },
  inject: [
    'modalState',
    'resetModalState',
    'setModalState',
    'setModalStateProperty',
    'showFileDialog',
    'updateToolPanel',
    'wrapperModal'
  ],
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      base64: '',
      children: [],
      error: '',
      file: '',
      page: 1,
      pdfLoaded: false,
      presets: null,
      rotation: 0,
      size: {
        height: 0,
        width: 0
      },
      viewerKey: 0,
      zoomQuantity: ZOOM_STEP,
    }
  },
  computed: {
    ...mapGetters(['isActionAllowed']),
    styles() {
      return {
        height: `${this.size.height}px`,
        width: `${this.size.width}px`
      }
    },
    fileLoaded() {
      return this.presets && this.presets.file !== ''
    },
    emptySize() {
      const scale = ((this.size.height - 50) / A4_HEIGHT)
      return {
        width: A4_WIDTH * scale,
        height: A4_HEIGHT * scale,
      }
    }
  },
  methods: {
    async _viewerUpdate(PDFJSObject) {
      await this.updateToolPanel({ PDFJSObject })
      this.pdfLoaded = true
      this.wrapperModal._data.isLoading = false
      if (PDFJSObject.page.pdfPageRotate > 0) this.rotation = PDFJSObject.page.pdfPageRotate * -1
      this.getThumbnailList()
    },
    _updateInfo() {
      if (isEqual(this.presets, this.modalState.config)) return
      const { file, page, base64, rotation, size, PDFJSObject } = this.modalState.config
      this.size = size
      if (file) {
        this.base64 = base64
        this.file = file
        this.page =
          PDFJSObject && page === -1
            ? convertLastPage(page, PDFJSObject.document._pdfInfo.numPages)
            : page
        this.rotation = rotation
        if (this.presets && this.presets.base64 !== base64) this.viewerKey += 1
      }
      this.presets = this.modalState.config
    },
    async buildThumbnailsPreview() {
      const colors = {
        employee: '#54caa7',
        employer: '#6a5eb4'
      }
      let thumbs = {}
      for (const name of Object.keys(this.modalState.config.thumbnails)) {
        const thumb = this.modalState.config.thumbnails[name]
        if (thumb && thumb.show) {
          const key = findKey(thumbs, { page: thumb.page })
          const signature = [{
            color: colors[name],
            x: thumb.x,
            y: thumb.y,
            show: thumb.show
          }]
          if (!key) {
            thumbs = Object.assign({}, thumbs, {
              [name]: {
                img: thumb.preview,
                page: thumb.page,
                signature
              }
            })
          } else {
            thumbs = {
              thumbnail: {
                img: thumbs[key].img,
                signature: signature.concat(thumbs[key].signature)
              }
            }
          }
        }
      }
      if (isEmpty(thumbs)) {
        thumbs = {
          thumbnail: this.modalState.config.thumbnails.thumbnail
        }
      }
      const mapped = {}
      for (const name of Object.keys(thumbs)) {
        mapped[name] = await this.generateThumbnail(thumbs[name])
      }
      return mapped
    },
    async generateThumbnail(obj) {
      if (!this.modalState.config.PDFJSObject) return
      const { page, page: { viewport } } = this.modalState.config.PDFJSObject
      const scale = viewport.scale
      const ibm = await this.generateIBM(obj.img)
      const isPortrait = obj.img.width <= obj.img.height
      const newSize = isPortrait ? { h: 250, w: 177 } : { w: 250, h: 177 }
      const temp = document.createElement('canvas')
      const ctx = temp.getContext('2d')
      temp.width = newSize.w
      temp.height = newSize.h
      await ctx.drawImage(ibm, 0, 0, obj.img.width, obj.img.height, 0, 0, newSize.w, newSize.h)
      obj.signature.forEach((position) => {
        if (position.show) {
          const sx = obj.img.width / newSize.w
          const sy = obj.img.height / newSize.h
          const x = (position.x * scale) / sx
          const y = (position.y * scale) / sy
          const scaledWidth = (SQUARE_SIGNATURE_WIDTH * page.scale) / sx
          const scaledHeight = (SQUARE_SIGNATURE_HEIGHT * page.scale) / sy
          ctx.fillStyle = position.color
          ctx.fillRect(x, Math.abs(y), scaledWidth, scaledHeight)
        }
      })
      return temp.toDataURL().split(',')[1]
    },
    async generateIBM(imageData) {
      return await createImageBitmap(imageData, 0, 0, imageData.width, imageData.height)
    },
    async getThumbnailList() {
      const { employee, employer } = this.modalState.config
      const employeeThumb = await this.getThumbnailPage(this.modalState.config.employee.preset.page)
      const employerThumb = await this.getThumbnailPage(this.modalState.config.employer.preset.page)
      const thumbnail = await this.getBlankThumbnail(this.modalState.config.page)
      const employeePreview = { ...transformCoordinates(employee.preset, true), ...{ preview: employeeThumb }}
      const employerPreview = { ...transformCoordinates(employer.preset, true), ...{ preview: employerThumb }}
      await this.setModalStateProperty({ employee: employeePreview, employer: employerPreview, thumbnail }, 'thumbnails')
    },
    async getThumbnailPage(number) {
      if (!this.modalState.config.PDFJSObject) return
      const { page: { width: widthPage }, document: pdf } = this.modalState.config.PDFJSObject
      const numberConvert = convertLastPage(number, pdf._pdfInfo.numPages)
      const page = await pdf.getPage(numberConvert)
      const rotation = Math.abs(this.modalState.config.rotation)
      const unScaledViewport = page.getViewport({ scale: 1, rotation })
      const viewport = page.getViewport({ scale: (widthPage / unScaledViewport.width), rotation })
      const canvas = document.createElement('canvas')
      const canvasContext = canvas.getContext('2d')
      canvas.height = viewport.height
      canvas.width = viewport.width
      const renderContext = { canvasContext, viewport }
      await page.render(renderContext).promise
      return canvasContext.getImageData(0, 0, viewport.width, viewport.height)
    },
    async getBlankThumbnail(page) {
      const img = await this.getThumbnailPage(page)
      return { img, page, signature: [] }
    }
  }
}
</script>
<style scoped lang="scss">
.settings-content {
  overflow-y: auto;
  max-height: calc(100% - 6rem);
}
.settings-content::-webkit-scrollbar-track {
  background-color: #C4C4C4;
}
.settings-content::-webkit-scrollbar {
  width: 0.625rem;
}
.settings-content::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: #8C8C8C;
}
h2 {
  color: #333333
}
.bg-viewer {
  background: #717171;
}
.setting-wrapper {
  height: calc(100% - 1.25rem);
  border-radius: 5px;
}
.config-footer {
  background-color: #EEEEEE;
}
.input-number-wrapper {
  width: 33px;
  input {
    border-color: #4F41A3!important;
  }
}
.font-125 {
  font-size: 1.125rem;
}
::v-deep {
  .setting-button {
    width: 100%!important;
    justify-content: flex-start!important;
    font-size: 1.125rem!important;
    height: 50px!important;

    .zt-button-text {
      margin: 0 !important;
    }

    .zt-icon {
      position: absolute;
      right: 15px;
      top: 18px;
    }
  }
  .button-cancel {
    color: #707070!important;
    background: transparent!important;
    padding: 0!important;
    font-size: 1.125rem!important;

    .zt-button-text {
      margin: 0!important;
    }
  }
  .zt-modal {
    box-shadow: 0 3px 4px rgba(0, 0, 0, 0.14), 0 3px 3px rgba(0, 0, 0, 0.12),
    0 1px 8px rgba(0, 0, 0, 0.2);
  }
  .zt-modal.rounded {
    border-radius: .375rem!important;
  }
  button.button-cancel {
    color: #707070!important;
    background-color: transparent!important;
  }
  .zt-modal .zt-modal-body {
    height: calc(100% - 50px);
  }
  .rotate-button {
    font-size: 14px;
    color: #4F41A3;
    border-color: #4F41A3!important;
  }
}
</style>
